import { Minus, Plus } from 'lucide-react'
import { useEffect, useState } from 'react'
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl'

import useAddCartItem from '~/App/Cart/hooks/useAddCartItem'
import type { ProductsAdditionalFields } from '~/App/ChatThreads/@types'
import { Button } from '~/DesignSystem/components/button'
import { Card } from '~/DesignSystem/components/card'
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from '~/DesignSystem/components/input-group'
import { toast } from '~/DesignSystem/components/sonner'
import { cn } from '~/DesignSystem/lib/utils'

import { MAX_QUANTITY, MIN_QUANTITY } from './constants'
import { sanitizeQuantity } from './utils'

type ProductCardProps = {
  data: NonNullable<ProductsAdditionalFields['product_items']>[number]
}

const DEFAULT_QUANTITY = 1

function ProductCard({ data, ...props }: ProductCardProps) {
  const intl = useIntl()
  const [quantity, setQuantity] = useState(DEFAULT_QUANTITY)
  const price = data.sale_price_in_cents || data.price_in_cents

  const {
    mutate: addCartItem,
    isPending: addingCartItem,
    isSuccess: addCartItemSuccess,
    error: addCartItemError,
  } = useAddCartItem()

  const handleAddToCart = () => {
    addCartItem({
      variant_id: data.id,
      quantity,
    })
  }

  useEffect(() => {
    if (addCartItemSuccess) {
      setQuantity(DEFAULT_QUANTITY)
    }
  }, [addCartItemSuccess])

  useEffect(() => {
    if (addCartItemError) {
      toast.error(intl.formatMessage({ id: 'error.generic' }))
    }
  }, [addCartItemError])

  return (
    <Card className={cn('p-0 max-w-full grid grid-cols-3 gap-0 overflow-hidden')} {...props}>
      <div className="self-stretch">
        <img
          className="w-full h-full aspect-square object-cover"
          src={data.image_url}
          alt={data.name}
        />
      </div>
      <div className="flex flex-col gap-1 col-span-2 py-4 px-2">
        <a className="font-semibold hover:text-primary" href={data.url} target="_parent">
          {data.name}
        </a>
        {!!price && (
          <p className="text-base font-semibold text-primary">
            <FormattedNumber value={price / 100} currency={data.currency} style="currency" />
          </p>
        )}

        <div className="flex gap-1">
          <InputGroup>
            <InputGroupAddon>
              <InputGroupButton
                size="icon-xs"
                aria-label={intl.formatMessage({ id: 'productMessage.quantity.decrease' })}
                disabled={quantity <= MIN_QUANTITY || addingCartItem}
                onClick={() => setQuantity(sanitizeQuantity(quantity - 1))}
              >
                <Minus />
              </InputGroupButton>
            </InputGroupAddon>
            <InputGroupInput
              type="number"
              className="px-0 text-center no-spinner text-sm"
              disabled={addingCartItem}
              value={quantity}
              min={MIN_QUANTITY}
              max={MAX_QUANTITY}
              onChange={event => setQuantity(sanitizeQuantity(Number(event.target.value)))}
            />
            <InputGroupAddon align="inline-end">
              <InputGroupButton
                size="icon-xs"
                aria-label={intl.formatMessage({ id: 'productMessage.quantity.increase' })}
                disabled={quantity >= MAX_QUANTITY || addingCartItem}
                onClick={() => setQuantity(sanitizeQuantity(quantity + 1))}
              >
                <Plus />
              </InputGroupButton>
            </InputGroupAddon>
          </InputGroup>
          <Button onClick={handleAddToCart} disabled={addingCartItem}>
            <FormattedMessage id="productMessage.addToCart" />
          </Button>
        </div>
      </div>
    </Card>
  )
}

export { ProductCard }
